import { useState, useEffect, useMemo } from "react";
import { getClientMessages, API_BASE } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Loader2, Paperclip, Download, Search, Globe, Megaphone, Target,
  Search as SearchIcon, FileText,
} from "lucide-react";

const PRODUCT_LABEL = {
  marketing: "Marketing",
  web_design: "Web Design",
  seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID",
};
const PRODUCT_COLOR = {
  marketing: "bg-rose-100 text-rose-800 border-rose-200",
  web_design: "bg-blue-100 text-blue-800 border-blue-200",
  seo_aeo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  prospect_tools: "bg-purple-100 text-purple-800 border-purple-200",
};
const PRODUCT_ICON = { marketing: Megaphone, web_design: Globe, seo_aeo: SearchIcon, prospect_tools: Target };

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/**
 * ClientFilesPanel — shows every file attached across all messages/orders for a
 * given client. Chronological (newest first), searchable, filterable by product.
 *
 * Props:
 *   clientId (string, required)
 *   heightClass (string) — Tailwind class, default 'h-[600px]'
 */
export default function ClientFilesPanel({ clientId, heightClass = "h-[600px]" }) {
  const [threadData, setThreadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState("all");

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    getClientMessages(clientId)
      .then((r) => setThreadData(r.data))
      .catch(() => setThreadData(null))
      .finally(() => setLoading(false));
  }, [clientId]);

  const allFiles = useMemo(() => {
    if (!threadData) return [];
    const threadIndex = {};
    (threadData.threads || []).forEach((t) => { threadIndex[t.thread_id] = t; });
    const files = [];
    (threadData.messages || []).forEach((m) => {
      (m.attachments || []).forEach((att) => {
        const thread = threadIndex[m.project_id];
        files.push({
          id: att.id,
          filename: att.filename,
          author_name: m.author_name,
          created_at: m.created_at,
          product_type: m.product_type,
          order_name: thread ? (thread.product_type ? PRODUCT_LABEL[thread.product_type] : thread.name) : "—",
          order_id: m.project_id,
          message_body: m.body,
        });
      });
    });
    files.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return files;
  }, [threadData]);

  const filteredFiles = useMemo(() => {
    let out = allFiles;
    if (productFilter !== "all") {
      out = out.filter((f) => f.product_type === productFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((f) =>
        (f.filename || "").toLowerCase().includes(q) ||
        (f.author_name || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [allFiles, productFilter, search]);

  const activeProducts = threadData?.active_products || [];

  if (loading) {
    return (
      <Card className={`border flex items-center justify-center ${heightClass}`} data-testid="client-files-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  return (
    <Card className={`border flex flex-col overflow-hidden ${heightClass}`} data-testid="client-files-panel">
      <div className="p-3 border-b shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              Files ({allFiles.length})
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Every file attached in messages, across all orders for {threadData?.client_name}. Newest first.
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setProductFilter("all")}
              className={`text-xs px-2 py-0.5 rounded-full border ${productFilter === "all" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
              data-testid="files-product-all"
            >All</button>
            {activeProducts.map((p) => {
              const Icon = PRODUCT_ICON[p] || FileText;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProductFilter(p)}
                  className={`text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${productFilter === p ? "ring-2 ring-offset-1 ring-primary" : ""} ${PRODUCT_COLOR[p] || "bg-zinc-100"}`}
                  data-testid={`files-product-${p}`}
                >
                  <Icon className="h-3 w-3" />
                  {PRODUCT_LABEL[p]}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-2 relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by filename or sender..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-7 h-8 text-xs"
            data-testid="files-search-input"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="files-list">
        {filteredFiles.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10">
            {allFiles.length === 0
              ? "No files have been shared in this client's conversation yet."
              : "No files match your filters."}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-secondary/40 border-b text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Filename</th>
                <th className="text-left px-3 py-2 font-medium">Order</th>
                <th className="text-left px-3 py-2 font-medium">Shared by</th>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-right px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map((f) => (
                <tr key={`${f.id}-${f.created_at}`} className="border-b hover:bg-secondary/20 transition-colors" data-testid={`file-row-${f.id}`}>
                  <td className="px-3 py-2 max-w-[280px]">
                    <div className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium" title={f.filename}>{f.filename}</span>
                    </div>
                    {f.message_body && (
                      <div className="text-[10px] text-muted-foreground truncate mt-0.5 pl-5" title={f.message_body}>
                        {f.message_body}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {f.product_type ? (
                      <Badge variant="outline" className={`${PRODUCT_COLOR[f.product_type]} text-[10px] py-0 px-1.5 h-4`}>
                        {PRODUCT_LABEL[f.product_type]}
                      </Badge>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{f.author_name || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{formatDate(f.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      className="h-7 text-xs"
                      data-testid={`file-download-${f.id}`}
                    >
                      <a
                        href={`${API_BASE}/api/files/${f.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Download ${f.filename}`}
                      >
                        <Download className="h-3 w-3 mr-1" /> Download
                      </a>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
